import React, { Component } from "react";
import styled from "styled-components";

const FONT = "'Mukta Vaani', Lato, sans-serif";
const ACCENT_DARK = "#3A507B";
const INK = "rgba(0, 27, 59, 0.94)";
const INK_MUTED = "rgba(0, 27, 59, 0.7)";
const INK_SUBTLE = "rgba(0, 27, 59, 0.5)";
const BORDER = "#E5E9F0";

const Spotlight = styled.div`
  position: fixed;
  box-shadow:
    0 0 0 4px rgba(82, 197, 205, 0.9),
    0 0 0 9999px rgba(0, 27, 59, 0.5);
  border-radius: 14px;
  z-index: 2000;
  pointer-events: none;
  transition: top 0.2s ease, left 0.2s ease, width 0.2s ease, height 0.2s ease;
`;

const FullOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 27, 59, 0.5);
  z-index: 2000;
  pointer-events: none;
`;

const Tooltip = styled.div`
  position: fixed;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 12px 32px rgba(0, 27, 59, 0.28);
  padding: 24px 26px;
  width: 546px;
  max-width: calc(100vw - 32px);
  max-height: calc(100vh - 32px);
  overflow-y: auto;
  z-index: 2002;
  font-family: ${FONT};
  transition: top 0.2s ease, left 0.2s ease;
`;

const StepIndicator = styled.div`
  font-family: ${FONT};
  font-size: 13px;
  font-weight: 600;
  color: ${INK_SUBTLE};
  letter-spacing: 0.08em;
  text-transform: uppercase;
  margin-bottom: 10px;
`;

const Title = styled.h3`
  font-family: ${FONT};
  font-weight: 700;
  font-size: 20px;
  line-height: 26px;
  color: ${INK};
  margin: 0 0 12px;
`;

const Body = styled.div`
  font-family: ${FONT};
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: ${INK_MUTED};

  p {
    margin: 0 0 12px;
  }

  p:last-child {
    margin-bottom: 0;
  }

  ul {
    margin: 0 0 12px;
    padding-left: 20px;
  }

  li {
    margin: 0 0 6px;
  }

  li:last-child {
    margin-bottom: 0;
  }

  strong {
    color: ${INK};
    font-weight: 600;
  }
`;

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-top: 20px;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 10px;
`;

const Button = styled.button`
  font-family: ${FONT};
  font-weight: 600;
  font-size: 15px;
  padding: 10px 20px;
  border-radius: 8px;
  border: 1.5px solid transparent;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease, opacity 0.15s ease;
`;

const BackButton = styled(Button)`
  background: #fff;
  color: ${INK_MUTED};
  border-color: ${BORDER};

  &:hover:not(:disabled) {
    border-color: rgba(0, 27, 59, 0.3);
    color: ${INK};
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const NextButton = styled(Button)`
  background: ${ACCENT_DARK};
  color: #fff;

  &:hover {
    background: #2A4169;
  }
`;

const SkipLink = styled.button`
  background: none;
  border: none;
  color: ${INK_SUBTLE};
  font-family: ${FONT};
  font-size: 15px;
  cursor: pointer;
  padding: 6px 4px;

  &:hover {
    color: ${INK};
    text-decoration: underline;
  }
`;

const PADDING = 12;
const TOOLTIP_WIDTH = 546;
const TOOLTIP_HEIGHT_GUESS = 320;
const GAP = 20;

class ProjectionsTour extends Component {
  constructor(props) {
    super(props);
    this.state = {
      step: 0,
      targetRect: null,
      tooltipPos: { top: 0, left: 0 },
    };
  }

  componentDidMount() {
    if (this.props.active) {
      this.attachListeners();
      // Target may not be in the DOM for the first paint; measure
      // after the modal has committed its layout.
      this.rafId = window.requestAnimationFrame(this.measure);
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (!prevProps.active && this.props.active) {
      this.attachListeners();
      this.setState({ step: 0 }, () => {
        this.rafId = window.requestAnimationFrame(this.measure);
      });
    } else if (prevProps.active && !this.props.active) {
      this.detachListeners();
    } else if (prevState.step !== this.state.step && this.props.active) {
      this.rafId = window.requestAnimationFrame(this.measure);
    }
  }

  componentWillUnmount() {
    this.detachListeners();
    if (this.rafId) window.cancelAnimationFrame(this.rafId);
  }

  attachListeners = () => {
    window.addEventListener("resize", this.measure);
    window.addEventListener("scroll", this.measure, true);
  };

  detachListeners = () => {
    window.removeEventListener("resize", this.measure);
    window.removeEventListener("scroll", this.measure, true);
  };

  measure = () => {
    const { steps } = this.props;
    const { step } = this.state;
    if (!steps || !steps[step]) return;

    const currentStep = steps[step];

    // Intro / outro steps that aren't tied to any card: render the
    // tooltip centered in the viewport with the dimming overlay but
    // no spotlight ring.
    if (!currentStep.targetId) {
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const left = Math.max(PADDING, (vw - TOOLTIP_WIDTH) / 2);
      const top = Math.max(PADDING, (vh - TOOLTIP_HEIGHT_GUESS) / 2);
      this.setState({ targetRect: null, tooltipPos: { top, left } });
      return;
    }

    const target = document.getElementById(currentStep.targetId);
    if (!target) {
      this.setState({ targetRect: null });
      return;
    }

    const rect = target.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    // Preferred placement: right of the target. If the tooltip would
    // run past the viewport on the right, try the left side of the
    // target instead. Only fall back to a below-the-target placement
    // when neither side has room (e.g. narrow viewports).
    let left = rect.right + GAP;
    let top = rect.top;

    const fitsRight = left + TOOLTIP_WIDTH + PADDING <= vw;
    if (!fitsRight) {
      const leftOfTarget = rect.left - GAP - TOOLTIP_WIDTH;
      if (leftOfTarget >= PADDING) {
        left = leftOfTarget;
        top = rect.top;
      } else {
        left = Math.max(PADDING, rect.left);
        top = rect.bottom + GAP;
      }
    }

    if (top + TOOLTIP_HEIGHT_GUESS > vh - PADDING) {
      top = Math.max(PADDING, vh - TOOLTIP_HEIGHT_GUESS - PADDING);
    }

    this.setState({
      targetRect: {
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
      },
      tooltipPos: { top, left },
    });
  };

  next = () => {
    const { step } = this.state;
    const { steps, onEnd } = this.props;
    if (step >= steps.length - 1) {
      if (onEnd) onEnd();
      return;
    }
    this.setState({ step: step + 1 });
  };

  back = () => {
    const { step } = this.state;
    if (step <= 0) return;
    this.setState({ step: step - 1 });
  };

  skip = () => {
    const { onEnd } = this.props;
    if (onEnd) onEnd();
  };

  render() {
    const { active, steps } = this.props;
    const { step, targetRect, tooltipPos } = this.state;
    if (!active || !steps || steps.length === 0) return null;

    const current = steps[step];
    if (!current) return null;

    const isLast = step === steps.length - 1;
    const isFirst = step === 0;

    return (
      <div>
        {targetRect ? (
          <Spotlight
            style={{
              top: targetRect.top - 6,
              left: targetRect.left - 6,
              width: targetRect.width + 12,
              height: targetRect.height + 12,
            }}
          />
        ) : (
          <FullOverlay />
        )}
        <Tooltip
          style={{
            top: tooltipPos.top,
            left: tooltipPos.left,
          }}
        >
          <StepIndicator>
            Step {step + 1} of {steps.length}
          </StepIndicator>
          <Title>{current.title}</Title>
          <Body>{current.body}</Body>
          <Footer>
            <SkipLink onClick={this.skip}>Skip tour</SkipLink>
            <ButtonRow>
              <BackButton onClick={this.back} disabled={isFirst}>Back</BackButton>
              <NextButton onClick={this.next}>
                {isLast ? "Got it" : "Next"}
              </NextButton>
            </ButtonRow>
          </Footer>
        </Tooltip>
      </div>
    );
  }
}

export default ProjectionsTour;



// WEBPACK FOOTER //
// ./src/components/ceo/decisions/projections-tour.js